import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";
import { vacancySchema, type VacancyFormData } from "@shared/types";
import { apiClient } from "@/lib/api";
import type { Vacancy } from "@shared/types";

export default function VacancyFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(isEdit);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<VacancyFormData>({
    resolver: zodResolver(vacancySchema),
    defaultValues: {
      title: "",
      titleEs: "",
      description: "",
      descriptionEs: "",
      requirements: "",
      requirementsEs: "",
      payRate: "",
      location: "",
      isActive: true,
    },
  });

  const isActive = watch("isActive");

  useEffect(() => {
    if (isEdit && id) {
      loadVacancy(parseInt(id));
    }
  }, [id]);

  const loadVacancy = async (vacancyId: number) => {
    try {
      const response = await apiClient.admin.vacancies.get(vacancyId);
      const vacancy = response.data as Vacancy;
      reset({
        title: vacancy.title,
        titleEs: vacancy.titleEs || "",
        description: vacancy.description,
        descriptionEs: vacancy.descriptionEs || "",
        requirements: vacancy.requirements || "",
        requirementsEs: vacancy.requirementsEs || "",
        payRate: vacancy.payRate || "",
        location: vacancy.location || "",
        isActive: vacancy.isActive,
      });
    } catch (error) {
      console.error("Failed to load vacancy:", error);
      toast.error("Failed to load vacancy");
      navigate("/admin/vacancies");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: VacancyFormData) => {
    try {
      if (isEdit && id) {
        await apiClient.admin.vacancies.update(parseInt(id), data);
        toast.success("Vacancy updated");
      } else {
        await apiClient.admin.vacancies.create(data);
        toast.success("Vacancy created");
      }
      navigate("/admin/vacancies");
    } catch (error) {
      console.error("Failed to save vacancy:", error);
      toast.error("Failed to save vacancy");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/admin/vacancies")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">
            {isEdit ? "Edit Vacancy" : "Create Vacancy"}
          </h1>
          <p className="text-muted-foreground">
            {isEdit ? "Update vacancy details" : "Publish a new job posting"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title (English) *</Label>
                <Input
                  id="title"
                  placeholder="e.g. OTR Truck Driver"
                  {...register("title")}
                />
                {errors.title && (
                  <p className="text-sm text-destructive">{errors.title.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="titleEs">Title (Spanish)</Label>
                <Input
                  id="titleEs"
                  placeholder="e.g. Conductor de Camión OTR"
                  {...register("titleEs")}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  placeholder="e.g. Houston, TX"
                  {...register("location")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="payRate">Pay Rate</Label>
                <Input
                  id="payRate"
                  placeholder="e.g. $0.55/mile or $25/hr"
                  {...register("payRate")}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Description */}
        <Card>
          <CardHeader>
            <CardTitle>Description</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="description">Description (English) *</Label>
                <Textarea
                  id="description"
                  placeholder="Describe the position..."
                  rows={6}
                  {...register("description")}
                />
                {errors.description && (
                  <p className="text-sm text-destructive">
                    {errors.description.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="descriptionEs">Description (Spanish)</Label>
                <Textarea
                  id="descriptionEs"
                  placeholder="Describa el puesto..."
                  rows={6}
                  {...register("descriptionEs")}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Requirements */}
        <Card>
          <CardHeader>
            <CardTitle>Requirements</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="requirements">Requirements (English)</Label>
                <Textarea
                  id="requirements"
                  placeholder="List requirements..."
                  rows={6}
                  {...register("requirements")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="requirementsEs">Requirements (Spanish)</Label>
                <Textarea
                  id="requirementsEs"
                  placeholder="Liste los requisitos..."
                  rows={6}
                  {...register("requirementsEs")}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Status & Submit */}
        <Card>
          <CardHeader>
            <CardTitle>Publishing</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Checkbox
                id="isActive"
                checked={isActive}
                onCheckedChange={(checked) =>
                  setValue("isActive", checked === true)
                }
              />
              <Label htmlFor="isActive" className="cursor-pointer">
                Publish vacancy (visible to applicants)
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/admin/vacancies")}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                {isEdit ? "Update Vacancy" : "Create Vacancy"}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
